import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import { Checkbox } from '@atlaskit/checkbox';
import { CheckboxField, ErrorMessage, Field, Label } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import Select, { type OptionType, type ValueType } from '@atlaskit/select';
import TextField from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';
import { Flex } from '@atlassian/eoc-flex';

import { generateMinuteOptions } from '../../../utils';
import { FieldResetter } from '../../styled';

import messages from './messages';
import { MinuteInputWrapper, RepeatInputWrapper, SelectWrapper, Text } from './styled';
import { type RepeatEscalationInputsProps } from './types';

export const RepeatEscalationInputs: React.FC<RepeatEscalationInputsProps> = ({
	repeat,
	maxRepeatCount,
	unAckWhileRepeating,
	closeAlertWhenRepeatsCompleted,
}) => {
	const { formatMessage } = useIntl();
	const [isRepeating, setIsRepeating] = useState(repeat ? repeat > 0 : false);
	const [repeatError, setRepeatError] = useState<string>();

	const minuteOptions = generateMinuteOptions();

	const getDefaultMaxRepeatTime = () => {
		if (maxRepeatCount) {
			return minuteOptions.find((option) => option.value === maxRepeatCount);
		}

		return minuteOptions[0];
	};

	return (
		<FieldResetter>
			<Flex alignItems="flex-start" justifyContent="space-between">
				<Flex flexDirection="column" rowGap={2}>
					<Flex flexDirection="column" rowGap={0.5}>
						<Heading size="small">{formatMessage(messages.repeatEscalation)}</Heading>
						<Text>{formatMessage(messages.repeatEscalationDescription)}</Text>
					</Flex>
					<RepeatInputWrapper
						data-testid="escalation-policy-modal.repeat-wrapper"
						isVisible={isRepeating}
					>
						<Flex flexDirection="column" rowGap={2}>
							<div>
								<Label htmlFor="">{formatMessage(messages.repeat)}</Label>
								<Flex alignItems="center" columnGap={1}>
									{formatMessage(messages.repeatEscalationInput, {
										input: () => (
											<FieldResetter>
												<MinuteInputWrapper>
													<Field
														name="repeat"
														defaultValue={repeat ? repeat : isRepeating ? 1 : 0}
														validate={(val) => {
															if (val !== 0 && !val) {
																setRepeatError(formatMessage(messages.fieldRequired));
																return formatMessage(messages.fieldRequired);
															}

															if (val > 1440) {
																setRepeatError(formatMessage(messages.repeatMaxLimit));
																return formatMessage(messages.repeatMaxLimit);
															}

															if (val < 0) {
																setRepeatError(formatMessage(messages.repeatMinLimit));
																return formatMessage(messages.repeatMinLimit);
															}

															setRepeatError('');
															return '';
														}}
													>
														{({ fieldProps, error }) => (
															<TextField type="number" min="0" max="1440" {...fieldProps} />
														)}
													</Field>
												</MinuteInputWrapper>
											</FieldResetter>
										),
										select: () => (
											<FieldResetter>
												<SelectWrapper>
													<Field<ValueType<OptionType, false>>
														name="maxRepeatCount"
														defaultValue={getDefaultMaxRepeatTime()}
													>
														{({ fieldProps }) => (
															<Select
																menuPosition="fixed"
																options={minuteOptions}
																{...fieldProps}
															/>
														)}
													</Field>
												</SelectWrapper>
											</FieldResetter>
										),
									})}
								</Flex>
								{repeatError && <ErrorMessage>{repeatError}</ErrorMessage>}
							</div>
							<div>
								<Label htmlFor="">{formatMessage(messages.actions)}</Label>
								<FieldResetter>
									<CheckboxField
										name="unAckWhileRepeating"
										defaultIsChecked={unAckWhileRepeating || false}
									>
										{({ fieldProps }) => (
											<Checkbox
												{...fieldProps}
												label={formatMessage(messages.ifAlertIsNotClosed)}
											/>
										)}
									</CheckboxField>
								</FieldResetter>
								<CheckboxField
									name="closeAlertWhenRepeatsCompleted"
									defaultIsChecked={closeAlertWhenRepeatsCompleted || false}
								>
									{({ fieldProps }) => (
										<Checkbox {...fieldProps} label={formatMessage(messages.ifEscalationRepeats)} />
									)}
								</CheckboxField>
							</div>
						</Flex>
					</RepeatInputWrapper>
				</Flex>
				<div>
					<Field<boolean> name="repeatEnabled" defaultValue={repeat !== 0}>
						{({ fieldProps: { onChange, ...fieldProps } }) => (
							<Toggle
								{...fieldProps}
								label={formatMessage(messages.repeatEscalation)}
								isChecked={isRepeating}
								value={String(fieldProps.value)}
								onChange={(e) => {
									setIsRepeating(e.currentTarget.checked);
									onChange(e);
								}}
							/>
						)}
					</Field>
				</div>
			</Flex>
		</FieldResetter>
	);
};
